/**
 * Shared centerline polydata construction utilities.
 */

import { vec3, mat4 } from 'gl-matrix';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import vtkPolyData from 'vtk.js/Sources/Common/DataModel/PolyData';
import { CenterlineDistanceBranchSelection } from '../CenterlineMeasurement/CenterlinePlaneShared';
import { clamp } from './Common';

/**
 * Write a 4x4 matrix built from tangent/bitangent/normal/position [T, B, N, P]
 * into the packed orientations buffer at slot `k`.
 *
 * Layout (column-major, matching gl-matrix):
 *   Column 0 (indices 0-2): T (tangent — along-vessel direction)
 *   Column 1 (indices 4-6): B (bitangent — cross-section sampling direction)
 *   Column 2 (indices 8-10): N (normal — completes right-hand frame: N = T × B)
 *   Column 3 (indices 12-14): P (position)
 *
 * @param {Float32Array} orientations - Buffer to write into.
 * @param {number} k - Matrix index.
 * @param {import('gl-matrix').vec3} T
 * @param {import('gl-matrix').vec3} B
 * @param {import('gl-matrix').vec3} N
 * @param {Float32Array|number[]} p - Position [x,y,z].
 */
function emitMatrix(orientations, k, T, B, N, p) {
    const m = mat4.create();
    m[0] = T[0]; m[1] = T[1]; m[2] = T[2];
    m[4] = B[0]; m[5] = B[1]; m[6] = B[2];
    m[8] = N[0]; m[9] = N[1]; m[10] = N[2];
    m[12] = p[0]; m[13] = p[1]; m[14] = p[2]; m[15] = 1;
    orientations.set(m, k * 16);
}

/**
 * Extract a sign-consistent quaternion to guarantee the fragment-shader SLERP
 * always takes the short arc, enforced by flipping q when dot(q_{k-1}, q_k) < 0.
 *
 * @param {Float32Array} quats   - Output quaternion buffer (4 floats per slot).
 * @param {number}       k       - Slot index to write.
 * @param {Float32Array} mats    - Mat4 buffer (16 floats per slot) written by emitMatrix.
 * @param {number[]}     tmp     - Reusable 4-element work array.
 */
function emitQuat(quats, k, mats, tmp) {
    mat4.getRotation(tmp, mats.subarray(k * 16, k * 16 + 16));
    const len = Math.sqrt(tmp[0]*tmp[0] + tmp[1]*tmp[1] + tmp[2]*tmp[2] + tmp[3]*tmp[3]);
    if (len > 1e-10) { tmp[0] /= len; tmp[1] /= len; tmp[2] /= len; tmp[3] /= len; }
    if (k > 0) {
        const prev = k * 4 - 4;
        if (quats[prev]*tmp[0] + quats[prev+1]*tmp[1] + quats[prev+2]*tmp[2] + quats[prev+3]*tmp[3] < 0) {
            tmp[0] = -tmp[0]; tmp[1] = -tmp[1]; tmp[2] = -tmp[2]; tmp[3] = -tmp[3];
        }
    }
    quats[k * 4]     = tmp[0];
    quats[k * 4 + 1] = tmp[1];
    quats[k * 4 + 2] = tmp[2];
    quats[k * 4 + 3] = tmp[3];
}

/**
 * Parallel-transport the frame's bitangent B from orientation with Tprev to
 * orientation with T, minimizing twist.  Mutates B in-place.
 *
 * @param {import('gl-matrix').vec3} Tprev
 * @param {import('gl-matrix').vec3} T
 * @param {import('gl-matrix').vec3} B - Bitangent to rotate (mutated).
 */
function transportFrame(Tprev, T, B) {
    const axis = vec3.cross([], Tprev, T);
    const s = vec3.len(axis);
    let c = vec3.dot(Tprev, T);
    c = clamp(c, -1, 1);

    if (s > 1e-8) {
        const k = vec3.scale([], axis, 1 / s);
        const theta = Math.atan2(s, c);

        const kxB = vec3.cross([], k, B);
        const kv = vec3.dot(k, B);
        const term1 = vec3.scale([], B, Math.cos(theta));
        const term2 = vec3.scale([], kxB, Math.sin(theta));
        const term3 = vec3.scale([], k, kv * (1 - Math.cos(theta)));
        vec3.add(B, term1, term2);
        vec3.add(B, B, term3);
    }

    // Re-orthonormalize B against T.
    const proj = vec3.scale([], T, vec3.dot(B, T));
    vec3.sub(B, B, proj);

    if (vec3.len(B) < 1e-8) {
        vec3.set(B, 0, 1, 0);
        if (Math.abs(vec3.dot(B, T)) > 0.9) vec3.set(B, 0, 0, 1);
        const proj2 = vec3.scale([], T, vec3.dot(B, T));
        vec3.sub(B, B, proj2);
    }

    vec3.normalize(B, B);
}

/**
 * Build a vtkPolyData polyline from an array of point indices.
 *
 * Each point gets a rotation-minimizing frame (parallel transport) stored as a
 * 16-component mat4 tensor named 'Orientation'.
 *
 * @param {number[]} indices - Array of point indices for the polyline.
 * @param {Float32Array} points - Array of point positions from the source VTP.
 * @param {Float32Array|null} centerlineTangents - Optional tangents from the source VTP.
 * @param {number} lastTrunkMasterIndex - Last index of the trunk branch.
 * @returns {vtkPolyData}
 */
export function buildPolyline(indices, points, centerlineTangents, lastTrunkMasterIndex) {
    const n = indices.length;

    function createPositionsAndTangents() {
        const positions = new Float32Array(n * 3);
        const outTangents = centerlineTangents ? new Float32Array(n * 3) : null;

        const tmpVec = vec3.create();
        for (let i = 0; i < n; i++) {
            const pointIndex = indices[i];
            positions.set(points.subarray(3 * pointIndex, 3 * pointIndex + 3), 3 * i);

            if (outTangents) {
                tmpVec[0] = centerlineTangents[3 * pointIndex] || 0;
                tmpVec[1] = centerlineTangents[3 * pointIndex + 1] || 0;
                tmpVec[2] = centerlineTangents[3 * pointIndex + 2] || 0;
                vec3.normalize(tmpVec, tmpVec);
                outTangents.set(tmpVec, 3 * i);
            }
        }

        return { positions, tangents: outTangents };
    }

    const { positions, tangents } = createPositionsAndTangents();

    // Build rotation-minimizing frames (parallel transport).
    // For each frame we also extract a sign-consistent quaternion for the CPR mapper.
    //
    // vtkImageCPRMapper calls mat4.getRotation independently on each per-point mat4,
    // which can produce antipodal quaternions (q vs -q) for consecutive frames when the
    // rotation-matrix trace (T[0]+B[1]+N[2]) oscillates around zero.
    const orientations = new Float32Array(n * 16);
    const quatOrientations = new Float32Array(n * 4);
    const q = [0, 0, 0, 0]; // reused per-iteration, no heap allocation
    const T = vec3.create();
    const Tprev = vec3.create();
    const B = vec3.create();
    const N = vec3.create();

    // Initialize T0.
    {
        const p0 = positions.subarray(0, 3);
        const p1 = positions.subarray(3, 6);
        if (tangents) {
            vec3.set(T, tangents[0], tangents[1], tangents[2]);
        } else {
            vec3.subtract(T, p1, p0);
        }
        if (vec3.len(T) === 0) vec3.set(T, 1, 0, 0);
        vec3.normalize(T, T);
        vec3.copy(Tprev, T);
    }

    // Choose initial B0 perpendicular to T0.
    vec3.set(B, 1, 0, 0);
    if (Math.abs(vec3.dot(B, T)) > 0.9) vec3.set(B, 0, 1, 0);
    const tmp = vec3.scale([], T, vec3.dot(B, T));
    vec3.sub(B, B, tmp);
    if (vec3.len(B) === 0) vec3.set(B, 0, 0, 1);
    vec3.normalize(B, B);
    vec3.cross(N, T, B);
    vec3.normalize(N, N);

    emitMatrix(orientations, 0, T, B, N, positions.subarray(0, 3));
    emitQuat(quatOrientations, 0, orientations, q);

    // March along the polyline with minimal twist.
    for (let i = 1; i < n; ++i) {
        const p = positions.subarray(3 * i, 3 * i + 3);
        if (tangents) {
            vec3.set(T, tangents[3 * i], tangents[3 * i + 1], tangents[3 * i + 2]);
        } else {
            const pPrev = positions.subarray(3 * (i - 1), 3 * (i - 1) + 3);
            vec3.subtract(T, p, pPrev);
        }
        if (vec3.len(T) === 0) vec3.copy(T, Tprev);
        vec3.normalize(T, T);

        if (vec3.dot(T, Tprev) < 0) vec3.scale(T, T, -1);

        transportFrame(Tprev, T, B);

        vec3.cross(N, T, B);
        vec3.normalize(N, N);

        emitMatrix(orientations, i, T, B, N, p);
        emitQuat(quatOrientations, i, orientations, q);
        vec3.copy(Tprev, T);
    }

    const out = vtkPolyData.newInstance();
    out.getPoints().setData(positions, 3);
    const lines = new Uint32Array(1 + n);
    lines[0] = n;
    for (let i = 0; i < n; i++) lines[1 + i] = i;
    out.getLines().setData(lines);

    out.getPointData().setTensors(
        vtkDataArray.newInstance({
            name: 'Orientation',
            numberOfComponents: 16,
            values: orientations,
        })
    );

    out.getPointData().addArray(
        vtkDataArray.newInstance({
            name: 'OrientationQuat',
            numberOfComponents: 4,
            values: quatOrientations,
        })
    );

    out.getFieldData().addArray(
        vtkDataArray.newInstance({
            name: 'LastTrunkMasterIndex',
            numberOfComponents: 1,
            values: new Float32Array([lastTrunkMasterIndex]),
        })
    );

    return out;
}

/**
 * Build centerline polylines for Left, Right and Cusp branches from the wall
 * centerline VTP.
 *
 * @param {vtkPolyData} wallCenterline
 * @returns {{
 *   centerlinesPolyData: Record<string, vtkPolyData>,
 *   branchOriginalIndices: Record<string, number[]>
 * }}
 */
export function buildCenterlinesPolyData(wallCenterline) {
    function getFieldData(name) {
        const array = wallCenterline.getFieldData().getArrayByName(name);
        return array ? Array.from(array.getData()) : null;
    }

    const distanceBranchTrunk = getFieldData('Distance_Branch_Trunk');
    const distanceBranchLeft = getFieldData('Distance_Branch_Left');
    const distanceBranchRight = getFieldData('Distance_Branch_Right');
    const distanceBranchCusp = getFieldData('Distance_Branch_Trunk_Cusp');

    const leftIndices = [...distanceBranchTrunk, ...distanceBranchLeft];
    const rightIndices = [...distanceBranchTrunk, ...distanceBranchRight];
    const cuspIndices = [...distanceBranchCusp];

    const branchOriginalIndices = {
        Left: leftIndices,
        Right: rightIndices,
        Cusp: cuspIndices
    };

    const normalsPointData = wallCenterline.getPointData().getArrayByName('Normals');
    const pts = wallCenterline.getPoints().getData();
    const tangentsFromVTP = normalsPointData ? normalsPointData.getData().map((x) => -x) : null;

    const lastTrunkMasterIndex = distanceBranchTrunk[distanceBranchTrunk.length - 1];

    const centerlinesPolyData = {
        [CenterlineDistanceBranchSelection.LEFT]: buildPolyline(leftIndices, pts, tangentsFromVTP, lastTrunkMasterIndex),
        [CenterlineDistanceBranchSelection.RIGHT]: buildPolyline(rightIndices, pts, tangentsFromVTP, lastTrunkMasterIndex),
        [CenterlineDistanceBranchSelection.CUSP]: buildPolyline(cuspIndices, pts, tangentsFromVTP, lastTrunkMasterIndex)
    };

    return {
        centerlinesPolyData,
        branchOriginalIndices,
    };
}

/**
 * Extract the orientation frame at a given compacted index from a centerline
 * polydata's Orientation tensor array.
 *
 * @param {vtkPolyData} centerlinePolyData - Branch centerline polydata with Orientation tensors.
 * @param {number} index - Compacted (branch-local) point index.
 * @returns {{ T: Float32Array, B: Float32Array, N: Float32Array, P: Float32Array }}
 */
function getOrientationFrameAtIndex(centerlinePolyData, index) {
    const orientations = centerlinePolyData.getPointData().getTensors().getData();
    const numPoints = centerlinePolyData.getPoints().getNumberOfPoints();
    const idx = Math.max(0, Math.min(index, numPoints - 1));
    const offset = idx * 16;

    // From emitMatrix layout:
    // Column 0 (indices 0-2): T (tangent)
    // Column 1 (indices 4-6): B (bitangent)
    // Column 2 (indices 8-10): N (normal)
    // Column 3 (indices 12-14): P (position)
    return {
        T: orientations.subarray(offset, offset + 3),
        B: orientations.subarray(offset + 4, offset + 7),
        N: orientations.subarray(offset + 8, offset + 11),
        P: orientations.subarray(offset + 12, offset + 15),
    };
}

const computeFrameScratchProj = vec3.create();

/**
 * Compute the orientation frame at a given index from the actual point
 * positions of a centerline polydata.
 *
 * This is necessary when point positions have been edited but the Orientation
 * tensors have not been recomputed.
 *
 * @param {vtkPolyData} polyData - Centerline polydata (points may be edited).
 * @param {number} index - Branch-local point index.
 * @returns {{ T: Float32Array|vec3, B: vec3, N: vec3, P: Float32Array }}
 */
export function computeFrameAtIndex(polyData, index) {
    const pts = polyData.getPoints().getData();
    const numPts = polyData.getPoints().getNumberOfPoints();
    const idx = Math.max(0, Math.min(index, numPts - 1));

    const P = pts.subarray(3 * idx, 3 * idx + 3);

    // Compute tangent from finite differences of the current (possibly edited)
    // point positions.
    const T = vec3.create();
    if (numPts <= 1) {
        vec3.set(T, 0, 0, 1);
    } else if (idx === 0) {
        vec3.subtract(T, pts.subarray(3, 6), pts.subarray(0, 3));
    } else if (idx === numPts - 1) {
        vec3.subtract(T,
            pts.subarray(3 * idx, 3 * idx + 3),
            pts.subarray(3 * (idx - 1), 3 * (idx - 1) + 3));
    } else {
        // Central difference for interior points.
        vec3.subtract(T,
            pts.subarray(3 * (idx + 1), 3 * (idx + 1) + 3),
            pts.subarray(3 * (idx - 1), 3 * (idx - 1) + 3));
    }
    if (vec3.len(T) < 1e-12) vec3.set(T, 0, 0, 1);
    vec3.normalize(T, T);

    // Ensure T is consistent with the stored tangent direction (avoid flips).
    const storedFrame = getOrientationFrameAtIndex(polyData, idx);
    if (vec3.dot(T, storedFrame.T) < 0) {
        vec3.scale(T, T, -1);
    }

    // Project the stored bitangent B perpendicular to the new tangent.
    const B = vec3.fromValues(storedFrame.B[0], storedFrame.B[1], storedFrame.B[2]);
    vec3.scale(computeFrameScratchProj, T, vec3.dot(B, T));
    vec3.sub(B, B, computeFrameScratchProj);

    if (vec3.len(B) < 1e-8) {
        // Fallback: stored B is (nearly) parallel to new T.
        vec3.set(B, 0, 0, 1);
        if (Math.abs(vec3.dot(B, T)) > 0.9) vec3.set(B, 0, 1, 0);
        vec3.scale(computeFrameScratchProj, T, vec3.dot(B, T));
        vec3.sub(B, B, computeFrameScratchProj);
    }
    vec3.normalize(B, B);

    const N = vec3.cross(vec3.create(), T, B);
    vec3.normalize(N, N);

    return { T, B, N, P };
}

/**
 * Apply a `wallCenterlineUpdated` event payload to a wallCenterline dataSource.
 *
 * Each viewport owns a separate JS instance of the wallCenterline polydata. When
 * a centerline edit is committed, every viewport must mirror the new coordinates
 * (plus the derived Distances / Landmark_Lengths arrays, and optionally Normals)
 * onto its own dataSource before its next redraw, otherwise label positions and
 * cross-section plane orientations drift across viewports.
 *
 * @param {vtkPolyData|null|undefined} ds - The wallCenterline dataSource for this viewport.
 * @param {Object|null|undefined} detail - `event.detail` from the `wallCenterlineUpdated` event.
 * @param {{ includeNormals?: boolean }} [options] - Set `includeNormals` for viewports
 *   whose downstream filters (e.g. PlaneIndexFilter on the mesh viewport) read the
 *   Normals array to orient the cross-section plane.
 * @returns {boolean} True if the dataSource was updated and the caller should redraw.
 */
export function applyWallCenterlineUpdate(ds, detail, { includeNormals = false } = {}) {
    if (!ds || !detail) return false;
    const { outCoords, outNormals, outDistances, outLandmarkLengths } = detail;
    if (!outCoords) return false;

    // Defensive: skip if the dataSource was disposed between dispatch and handler.
    if (typeof ds.isDeleted === 'function' && ds.isDeleted()) return false;

    ds.getPoints().setData(outCoords, 3);

    if (includeNormals && outNormals) {
        const normalsArr = ds.getPointData().getArrayByName('Normals');
        if (normalsArr) {
            normalsArr.setData(outNormals, 3);
            normalsArr.modified();
        }
    }
    if (outDistances) {
        const distArr = ds.getPointData().getArrayByName('Distances');
        if (distArr) {
            distArr.setData(outDistances, 1);
            distArr.modified();
        }
    }
    if (outLandmarkLengths) {
        const llArr = ds.getFieldData().getArrayByName('Landmark_Lengths');
        if (llArr) {
            llArr.setData(outLandmarkLengths, 1);
            llArr.modified();
        }
    }
    ds.modified();
    return true;
}

/**
 * Build a 4x4 reslice axes matrix for vtkImageReslice from an orientation frame.
 *
 * The resulting matrix defines a plane perpendicular to T (tangent) at position P,
 * with B as the output X axis and N as the output Y axis.
 *
 * @param {{ T: Float32Array, B: Float32Array, N: Float32Array, P: Float32Array }} frame
 * @returns {Float64Array} 16-element column-major mat4
 */
export function buildResliceAxesFromFrame(frame) {
    const { T, B, N, P } = frame;
    const axes = new Float64Array(16);
    // Column 0 = B (output X axis)
    axes[0] = B[0]; axes[1] = B[1]; axes[2] = B[2]; axes[3] = 0;
    // Column 1 = N (output Y axis)
    axes[4] = N[0]; axes[5] = N[1]; axes[6] = N[2]; axes[7] = 0;
    // Column 2 = T (through-plane / output Z)
    axes[8] = T[0]; axes[9] = T[1]; axes[10] = T[2]; axes[11] = 0;
    // Column 3 = P (origin)
    axes[12] = P[0]; axes[13] = P[1]; axes[14] = P[2]; axes[15] = 1;
    return axes;
}
